'use client'

import type { IconButtonProps } from '@chakra-ui/react'
import { IconButton } from '@chakra-ui/react'
import { MoonIcon, SunIcon } from 'lucide-react'
import { ThemeProvider, type ThemeProviderProps, useTheme } from 'next-themes'
import { forwardRef } from 'react'

export interface ColorModeProviderProps extends ThemeProviderProps {}

export function ColorModeProvider(props: ColorModeProviderProps) {
  return <ThemeProvider attribute="class" disableTransitionOnChange {...props} />
}

export type ColorMode = 'light' | 'dark'

export interface UseColorModeReturn {
  colorMode: ColorMode
  setColorMode: (colorMode: ColorMode) => void
  toggleColorMode: () => void
}

export function useColorMode(): UseColorModeReturn {
  const { resolvedTheme, setTheme } = useTheme()
  const toggleColorMode = () => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')
  }
  return {
    colorMode: resolvedTheme as ColorMode,
    setColorMode: setTheme,
    toggleColorMode,
  }
}

export function useColorModeValue<T>(light: T, dark: T) {
  const { colorMode } = useColorMode()
  return colorMode === 'dark' ? dark : light
}

export function ColorModeIcon() {
  const { colorMode } = useColorMode()
  return colorMode === 'dark' ? <MoonIcon /> : <SunIcon />
}

interface ColorModeButtonProps extends Omit<IconButtonProps, 'aria-label'> {}

export const ColorModeButton = forwardRef<HTMLButtonElement, ColorModeButtonProps>(
  function ColorModeButton(props, ref) {
    const { toggleColorMode } = useColorMode()
    return (
      <IconButton
        // `plain`, not `ghost`: ghost keeps a filled background after a click, which left
        // a grey square sitting under the icon while the neighbouring ui-library buttons
        // have none. Focus and active are pinned transparent for the same reason;
        // _focusVisible is untouched so keyboard focus is still shown.
        variant="plain"
        borderColor="transparent"
        _hover={{ bg: 'bg.subtle' }}
        _focus={{ bg: 'transparent' }}
        _active={{ bg: 'transparent' }}
        aria-label="Toggle color mode"
        // Match the ui-library Header.MenuButton footprint used by the other
        // top-bar-right controls (Links / Admin / Account) so the cluster is
        // visually uniform in both light and dark mode.
        boxSize="46px"
        borderRadius="2px"
        ref={ref}
        onClick={toggleColorMode}
        {...props}
        css={{ _icon: { width: '6', height: '6' } }}
      >
        <ColorModeIcon />
      </IconButton>
    )
  },
)
